import {
    Box, Text, Flex, Button, useDisclosure, IconButton,
    AlertDialog, AlertDialogOverlay, AlertDialogContent,
    AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import { MobileMenu } from './components/MobileMenu';
import { ApplicationRoutes } from 'utils/routes';
import { useParams } from 'react-router-dom';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from 'providers/Global';
import { AddressBookEntry } from 'types/addressBook';
import { getAddressBook, deleteAddressBookEntry } from 'api/addressBookAPI';
import { useToast } from 'hooks/useToast';
import AddressBookModal from 'components/Modals/AddressBookModal';
import { ReactComponent as DeleteIcon } from 'assets/delete-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as EditIcon } from 'assets/edit-icon.svg';
import copyToClipboard from 'copy-to-clipboard';
import { SCREEN_SM } from 'utils/constants';
import { useMediaQuery } from 'react-responsive';

const AddressBookPage = () => {
    const { walletId, multisigAccountId } = useParams();
    const { connection } = useContext(GlobalContext);
    const { showToast } = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [entries, setEntries] = useState<AddressBookEntry[]>([]);
    const [editEntry, setEditEntry] = useState<AddressBookEntry | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const cancelDeleteRef = useRef<HTMLButtonElement>(null);
    const limit = 10;

    const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getAddressBook(page, limit);
            if (response?.data?.entries) {
                setEntries(response.data.entries);
                setTotalPages(response.data.totalPages || 1);
            } else {
                setEntries([]);
            }
        } catch (e) {
            console.error('Failed to fetch address book:', e);
            setEntries([]);
        } finally {
            setIsLoading(false);
        }
    }, [page]);



    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handleAdd = () => {
        setEditEntry(null);
        onOpen();
    };

    const handleEdit = (entry: AddressBookEntry) => {
        setEditEntry(entry);
        onOpen();
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteAddressBookEntry(deleteId);
            showToast('Contact deleted', 'success', undefined, 'top');
            fetchEntries();
        } catch (e) {
            console.error('Failed to delete entry:', e);
            showToast('Failed to delete contact', 'error', undefined, 'top');
        } finally {
            setDeleteId(null);
        }
    };

    const handleSave = () => {
        fetchEntries();
    };

    return (
        <Flex flex={1} flexDirection="column">
            <MobileMenu
                pageName="Address Book"
                backUrl={`${ApplicationRoutes.Dashboard}/${walletId}/account/${multisigAccountId}`}
                onAction={handleAdd}
            />
            <Flex
                p={6}
                bg="white"
                height="full"
                borderRadius={16}
                flexDirection="column"
                boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
            >
                <Flex width="full" alignItems="center" h={10} gap={4} borderRadius={6} p={4} bg="primary" mb={2}>
                    <Box flex={3} isTruncated>
                        <Text size="xs">Name</Text>
                    </Box>
                    <Box flex={3} isTruncated>
                        <Text size="xs">Address</Text>
                    </Box>
                    {
                        !isScreenSmall && (
                            <Box flex={2} isTruncated>
                                <Text size="xs">Notes</Text>
                            </Box>
                        )
                    }
                    <Box w={isScreenSmall ? "80px" : "100px"} textAlign="right">
                        <Text size="xs">Actions</Text>
                    </Box>
                </Flex>

                <Box display="flex" width="full" flexDirection="column">
                    {isLoading ? (
                        <Flex justify="center" align="center" py={12}>
                            <Text size="sm" color="blackAlpha.500">Loading contacts...</Text>
                        </Flex>
                    ) : entries.length === 0 ? (
                        <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            py={20}
                            px={4}
                            textAlign="center"
                        >
                            <Text size="lg" variant="bodyBold" color="blackAlpha.800" mb={1}>
                                No contacts yet
                            </Text>
                            <Text size="sm" color="blackAlpha.600" mb={6}>
                                Your address book is empty
                            </Text>
                            <Button
                                variant="primary"
                                borderRadius={6}
                                onClick={handleAdd}
                                size="md"
                            >
                                Add Contact
                            </Button>
                        </Flex>
                    ) : (
                        entries.map((entry, index) => (
                            <Flex
                                key={entry._id || index}
                                gap={4}
                                minH={14}
                                p={4}
                                width="full"
                                borderRadius={8}
                                alignItems="center"
                                borderY="1px solid #F6F6F6"
                                _hover={{ bg: '#FAFAFA' }}
                            >
                                <Box flex={3} overflow="hidden">
                                    <Text size="xs" variant="bodyBold" isTruncated>{entry.name}</Text>
                                </Box>
                                <Box flex={3} display="flex" alignItems="center" gap={1} overflow="hidden">
                                    <Text size="xs" isTruncated>{entry.address}</Text>
                                    <CopyIcon
                                        width={16}
                                        height={16}
                                        cursor="pointer"
                                        onClick={() => {
                                            copyToClipboard(entry.address);
                                            showToast('Copied to clipboard', 'success', undefined, 'top');
                                        }}
                                    />
                                </Box>
                                {
                                    !isScreenSmall && (
                                        <Box flex={2} overflow="hidden">
                                            <Text size="xs" color="blackAlpha.600" isTruncated>{entry.notes || '—'}</Text>
                                        </Box>
                                    )
                                }
                                <Flex w={isScreenSmall ? "80px" : "100px"} gap={1} justifyContent="flex-end">
                                    <IconButton
                                        aria-label="Edit contact"
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => handleEdit(entry)}
                                        icon={<EditIcon width={14} height={14} />}
                                        h={8}
                                    />
                                    <IconButton
                                        aria-label="Delete contact"
                                        size="xs"
                                        variant="ghost"
                                        icon={<DeleteIcon width={14} height={14} />}
                                        onClick={() => setDeleteId(entry._id)}
                                        h={8}
                                    />
                                </Flex>
                            </Flex>
                        ))
                    )}
                </Box>

                {totalPages > 1 && (
                    <Flex justify="center" align="center" mt={6} gap={4}>
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            isDisabled={page === 1}
                        >
                            Previous
                        </Button>
                        <Text size="xs">Page {page} of {totalPages}</Text>
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            isDisabled={page === totalPages}
                        >
                            Next
                        </Button>
                    </Flex>
                )}
            </Flex>

            <AddressBookModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={handleSave}
                entry={editEntry}
                userAccountId={connection.accountId}
            />

            <AlertDialog
                isOpen={!!deleteId}
                leastDestructiveRef={cancelDeleteRef}
                onClose={() => setDeleteId(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius={16} mx={4}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Contact
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete this contact? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button
                                ref={cancelDeleteRef}
                                onClick={() => setDeleteId(null)}
                                borderRadius={6}
                                size="sm"
                            >
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                borderRadius={6}
                                size="sm"
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default AddressBookPage;
